import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  React.useEffect(() => {
    const { setCanonical } = require('../utils/seo');
    setCanonical('/privacy');
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-header">
        <Link to="/login" className="privacy-back-link">← Back</Link>
        <h1>💜 Privacy Policy</h1>
        <p className="privacy-subtitle">Your privacy is our absolute priority</p>
        <p className="privacy-effective">Effective Date: April 1, 2026 | Last Updated: May 6, 2026</p>
      </div>

      <div className="privacy-content">
        <section className="privacy-section">
          <h2>1. Who We Are</h2>
          <p>
            Belong ("we," "us," "our") is a privacy-first social platform that helps people with 
            invisible identities find their communities. This Privacy Policy explains how we collect, 
            use, protect, and share your information when you use our platform.
          </p>
          <p>
            We understand that the information you share with us is deeply personal and sensitive. 
            That's why privacy isn't just a feature — it's the foundation of everything we build.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><span className="data-label">Account Information:</span> Phone number, email address (optional), password</li>
            <li><span className="data-label">Profile Information:</span> First name, last name, gender, age, profession, photo (optional)</li>
            <li><span className="data-label">Location Information:</span> Hometown (city, state, country), current location (city, state, country)</li>
            <li><span className="data-label">Identity Information:</span> Cultural, health, interest, and other identity categories you choose to share (e.g., caste, mental health, neurodiversity)</li>
            <li><span className="data-label">Student Information:</span> College, graduation year, major (if applicable)</li>
            <li><span className="data-label">User-Generated Content:</span> Messages, community posts, group posts, event details, feedback, and reports</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li><span className="data-label">Usage Data:</span> Pages visited, features used, timestamps of activity</li>
            <li><span className="data-label">Device Information:</span> Browser type, operating system (collected as hashed values, not stored in plain text)</li>
            <li><span className="data-label">IP Address:</span> Stored only as a one-way hash for security monitoring — we cannot reverse it to identify you</li>
          </ul>

          <h3>2.3 Information We Do NOT Collect</h3>
          <ul>
            <li>We do not track your location in real-time</li>
            <li>We do not access your contacts or phone book</li>
            <li>We do not use cookies for advertising or tracking</li>
            <li>We do not sell or share your data with advertisers</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <ul>
            <li><span className="data-label">Identity Matching:</span> To connect you with people who share your identities — and only those identities</li>
            <li><span className="data-label">Account Security:</span> Phone/email verification, password protection, fraud detection</li>
            <li><span className="data-label">Communication:</span> Verification codes, connection notifications, new member alerts (with your consent)</li>
            <li><span className="data-label">Trust & Safety:</span> Trust score calculation, content moderation, abuse prevention, report handling</li>
            <li><span className="data-label">Platform Improvement:</span> Aggregated, anonymized analytics to improve features</li>
          </ul>
        </section>

        <section className="privacy-section privacy-highlight">
          <h2>4. Our Golden Privacy Rule</h2>
          <p className="golden-rule">
            🔒 We NEVER reveal identities that are not shared between users.
          </p>
          <p>
            If you and another person both share Identity A, you can see each other. But if you also 
            have Identity B that they don't share, they will never know about it. All identity filtering 
            happens at the database level — it's not just a UI choice, it's architecturally enforced.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. Progressive Disclosure — You Control What You Share</h2>
          <p>We use a three-level system so you decide when to share more:</p>
          <div className="disclosure-levels">
            <div className="disclosure-level">
              <span className="level-badge">Level 1</span>
              <span>After connection request: First name, city, state</span>
            </div>
            <div className="disclosure-level">
              <span className="level-badge">Level 2</span>
              <span>After acceptance: Last name</span>
            </div>
            <div className="disclosure-level">
              <span className="level-badge">Level 3</span>
              <span>Mutual opt-in only: Phone number, email, profile photo</span>
            </div>
          </div>
          <p>You must explicitly agree before any contact information is shared. Both parties must consent.</p>
        </section>

        <section className="privacy-section">
          <h2>6. Data Encryption & Security</h2>
          <ul>
            <li><span className="data-label">Sensitive Data Encryption:</span> Identity information (caste, health conditions, etc.) is encrypted using AES-256-GCM before storage</li>
            <li><span className="data-label">Password Hashing:</span> Passwords are hashed with bcrypt and never stored in plain text</li>
            <li><span className="data-label">Transport Security:</span> All data in transit is protected with HTTPS/TLS</li>
            <li><span className="data-label">Audit Logging:</span> All access to sensitive data is logged (with hashed identifiers, not plain text) for security monitoring</li>
            <li><span className="data-label">Rate Limiting:</span> API endpoints are rate-limited to prevent abuse and data scraping</li>
            <li><span className="data-label">JWT Authentication:</span> Secure token-based authentication with short-lived access tokens</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>7. Third-Party Services</h2>
          <p>We use the following third-party services to operate the platform:</p>
          <div className="third-party-list">
            <div className="third-party-item">
              <span className="tp-name">Brevo (Sendinblue)</span>
              <span className="tp-purpose">Email delivery for verification and notifications</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">Twilio</span>
              <span className="tp-purpose">SMS delivery for phone verification</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">OpenAI</span>
              <span className="tp-purpose">AI content moderation and quality filtering (post text only — no identity data sent)</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">Render</span>
              <span className="tp-purpose">Cloud hosting for our application and database</span>
            </div>
            <div className="third-party-item">
              <span className="tp-name">PostgreSQL</span>
              <span className="tp-purpose">Database storage with encryption at rest</span>
            </div>
          </div>
          <p>
            We do not share your identity information with any third party. Third-party services only 
            receive the minimum data needed to perform their function (e.g., Twilio receives your phone 
            number solely to send a verification code).
          </p>
        </section>

        <section className="privacy-section">
          <h2>8. Data Retention</h2>
          <ul>
            <li>Your account data is retained as long as your account is active</li>
            <li>Verification codes expire after 10 minutes and are automatically deleted</li>
            <li>Audit logs are retained for security purposes with hashed (non-reversible) identifiers</li>
            <li>If you delete your account, your personal data is removed. Some anonymized data may remain in aggregated statistics</li>
            <li>Messages between users are retained while both accounts are active. If you block a user, message history is hidden</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>9. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><span className="data-label">Access:</span> View all data we hold about you through your Profile page</li>
            <li><span className="data-label">Correction:</span> Update your profile information at any time</li>
            <li><span className="data-label">Deletion:</span> Request deletion of your account and associated data</li>
            <li><span className="data-label">Portability:</span> Request a copy of your data in a standard format</li>
            <li><span className="data-label">Withdraw Consent:</span> Disable discovery, change privacy settings, or delete your account</li>
            <li><span className="data-label">Object:</span> Opt out of non-essential communications</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href="mailto:belong@ourbelong.com">belong@ourbelong.com</a> or use the Contact Us page.</p>
        </section>

        <section className="privacy-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Belong is not intended for anyone under the age of 18. We do not knowingly collect 
            information from minors. If we learn that we have collected data from someone under 18, 
            we will delete it promptly.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Safety & Moderation</h2>
          <p>
            To keep our community safe, we may review reported content and user behavior. This includes:
          </p>
          <ul>
            <li>Reviewing reported messages and posts for violations</li>
            <li>Monitoring trust scores and suspicious activity patterns</li>
            <li>Taking action on accounts that violate community guidelines (warnings, suspensions, bans)</li>
          </ul>
          <p>
            Messages are not end-to-end encrypted, which allows our moderation team to review reported 
            content. We only access messages when a report is filed or suspicious activity is detected.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Belong for Business — Employee Privacy</h2>
          <p>
            When your employer uses Belong for Business, additional privacy protections apply:
          </p>
          <ul>
            <li><span className="data-label">Privacy Firewall:</span> Your employer CANNOT see your posts, comments, identities, connections, messages, or individual activity</li>
            <li><span className="data-label">Aggregated Metrics Only:</span> Org admins receive only anonymized, aggregated engagement data (e.g., "20 active users this week") — never individual data</li>
            <li><span className="data-label">K-Anonymity:</span> Metrics are only shown when enough users are in a group to prevent identification (minimum thresholds enforced)</li>
            <li><span className="data-label">Domain Verification:</span> Your work email is used solely to verify you belong to the organization — it is not shared with your employer</li>
            <li><span className="data-label">Separate Spaces:</span> Org community activity is isolated from your public Belong activity</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>13. Ambassador & Referral Program Data</h2>
          <p>
            If you participate in the Ambassador Program:
          </p>
          <ul>
            <li>We store your referral code, signup count, and activation metrics</li>
            <li>We send notification emails to your registered email when users sign up via your link</li>
            <li>We do NOT share the identity or personal information of referred users with you — you only see aggregate counts</li>
            <li>Referral data is used solely for attribution and reward calculation</li>
            <li>You may opt out of the program at any time by contacting belong@ourbelong.com</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>14. AI Content Moderation</h2>
          <p>
            We use OpenAI's APIs to help moderate content:
          </p>
          <ul>
            <li>Only post/comment text is sent to OpenAI — never your identity, name, email, or personal data</li>
            <li>Content is checked for harmful material, spam, and quality</li>
            <li>Per OpenAI's API data usage policy, content sent via their API is NOT used to train their models</li>
            <li>AI moderation is supplementary to human review — it does not make final decisions on account actions</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>15. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we make significant changes, 
            we will notify you through the app or via email. Your continued use of Belong after 
            changes are posted constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>16. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or how we handle your data, reach out:
          </p>
          <ul>
            <li>Email: <a href="mailto:belong@ourbelong.com">belong@ourbelong.com</a></li>
            <li>In-app: <Link to="/help">Contact Us</Link></li>
          </ul>
        </section>

        <div className="privacy-footer">
          <p>💜 Your trust means everything to us. We built Belong to be a safe space, and protecting your privacy is how we keep that promise.</p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
